import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ArrowLeft, Upload, Trash, Image as ImageIcon } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";

const SBL_LOGO = "/sbl-logo.jpeg";

export default function GalleryPage() {
  const { user } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const fetchGallery = () => {
    api.get("/gallery").then(r => { setGallery(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchGallery(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name.replace(/\.[^/.]+$/, ""));
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded to gallery");
      fetchGallery();
    } catch { toast.error("Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      toast.success("Image removed");
      setGallery(prev => prev.filter(g => g.id !== id));
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={SBL_LOGO} alt="SBL" className="w-9 h-9 object-contain rounded-sm" />
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              School Builder Lab Africa
            </span>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft size={14} /> Back to Home
          </Link>
        </div>
      </header>

      <div className="bg-slate-950 text-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-400 mb-3">Gallery</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Moments from Our Community
          </h1>
          <p className="text-base text-white/70 mt-4 max-w-xl mx-auto">
            A glimpse into the vibrant educational leadership experiences at School Builder Lab Africa.
          </p>
        </div>
      </div>

      <section className="py-16 sm:py-20 md:py-24" data-testid="gallery-full-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {isAdmin && (
            <div className="flex justify-end mb-6">
              <input type="file" ref={fileRef} accept="image/*" className="hidden" onChange={handleUpload} data-testid="gallery-page-upload-input" />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="rounded-none gap-2 text-sm"
                data-testid="gallery-page-upload-btn"
              >
                <Upload size={14} /> {uploading ? "Uploading..." : "Upload Image"}
              </Button>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[1,2,3,4,5,6].map(i => <div key={i} className="aspect-square bg-slate-100 animate-pulse" />)}
            </div>
          ) : gallery.length === 0 ? (
            <div className="text-center py-16">
              <ImageIcon size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">No gallery photos yet</p>
              <p className="text-sm text-slate-400">Check back soon for photos from our events and programmes.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {gallery.map((item, i) => (
                <div key={item.id} className="group relative overflow-hidden border border-slate-200 aspect-square" data-testid={`gallery-page-item-${i}`}>
                  <img
                    src={item.image_url || item.image_path}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-slate-950/70 px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-between">
                    <p className="text-xs text-white font-medium truncate">{item.title || ""}</p>
                    {isAdmin && (
                      <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-300 flex-shrink-0 ml-2" data-testid={`gallery-page-delete-${i}`}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8 text-center text-xs">
        <p>&copy; {new Date().getFullYear()} School Builder Lab Africa. All rights reserved.</p>
      </footer>
    </div>
  );
}
